import * as React from 'react';
import { injectStripe, CardElement, ReactStripeElements } from 'react-stripe-elements';
import FieldDecorator from '../../../commonComponents/fields/FieldDecorator';
import Loading from '../../../commonComponents/Loading';
import { StripeCardDetails } from '../../../models/StripeCustomer';
import Field from '../../../commonComponents/fields/Field';
import AsyncForm, { AsyncFormState } from '../../../commonComponents/forms/AsyncForm';
import { InputMaxLengthForAcic } from '../../../core/enums';
import { getEnglishNamesRegexString, englishNamesRegexErrorMessage } from '../../../core/domain';
import { hasValue } from '../../../lib/utils';
import DeleteButton from '../buttons/DeleteButton';
import './StripeCreditCardForm.less';
import Document from '../../../models/be/db/Document';
import PrimaryButton from '../buttons/PrimaryButton';

class StripeCreditCardForm extends AsyncForm<Props, State> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	constructor(props: any) {
		super(props);
		const { stripeCardDetails } = this.props;
		this.state = {
			...this.state,
			cardName: hasValue(stripeCardDetails) ? stripeCardDetails.cardName : null,
			isCardVerified: hasValue(stripeCardDetails) && hasValue(stripeCardDetails.stripeTokenId),
			errorMessage: null,
			loading: false
		};
	}

	handleVerifyCardButtonClick = async () => {
		if (this.state.loading) return;
		if (!hasValue(this.state.cardName)) {
			this.setState({ errorMessage: 'Card Name is required.' });
			return;
		}

		this.setState({ errorMessage: null });
		await this.performAction(async () => {
			const stripeResult: stripe.TokenResponse = await this.props.stripe.createToken({ name: this.state.cardName });
			if (stripeResult.error) {
				this.setState({ errorMessage: stripeResult.error.message });
				return;
			}
			const stripeCardDetails: StripeCardDetails = {
				cardName: this.state.cardName,
				stripeTokenId: stripeResult.token.id,
				stripeCardId: stripeResult.token.card.id
			};
			const document: Document = {
				documentType: this.props.cardType,
				description: null,
				stripeCardDetails,
				blob: null
			};
			if (await this.props.onVerifyCard(document)) {
				this.setState({ isCardVerified: true });
			}
		});
	};

	handleDeleteButtonClick = async () => {
		await this.performAction(async () => {
			this.props.onDeleteButtonClick(this.props.cardType);
			this.setState({ isCardVerified: false });
		});
	};

	createStripeElementStyle = (fontSize = '13px') => ({
		style: {
			base: {
				fontSize,
				color: '#54698D',
				letterSpacing: '0.05em',
				fontFamily: "'sofia-pro', sans-serif",
				'::placeholder': {
					color: '#AAB7C4'
				}
			}
		}
	});

	render() {
		const spacesBetweenNames = 2;
		const acicFullNameFieldMaxLength =
			InputMaxLengthForAcic.FirstName +
			InputMaxLengthForAcic.MiddleNames +
			InputMaxLengthForAcic.LastName +
			spacesBetweenNames;
		return (
			<>
				{this.props.stripe && (
					<>
						{/* Hack for form element css */}
						<div />
						{this.state.isCardVerified ? (
							<div className="verifiedResult">
								<p>
									<span className="capitalized">{this.props.cardType}</span> <span>- verified.</span>
								</p>
								<DeleteButton onClick={this.handleDeleteButtonClick} />
							</div>
						) : (
							<>
								<Field
									label="Name on Card"
									name="cardName"
									value={this.state.cardName}
									onValueChange={this.handleValueChange}
									maxLength={acicFullNameFieldMaxLength}
									pattern={getEnglishNamesRegexString()}
									patternErrorMessage={englishNamesRegexErrorMessage}
									required
									disabled={this.state.isCardVerified}
								/>
								<FieldDecorator label="Card Details">
									<CardElement hidePostalCode style={this.createStripeElementStyle().style} />
								</FieldDecorator>
								<p className="noticeMessage">
									Your card details are never charged by us, but stored by{' '}
									<a href="https://stripe.com/" target="_blank" rel="noreferrer">
										Stripe
									</a>{' '}
									for verification purposes.
								</p>
								{this.state.errorMessage && <p className="stripeFormErrorMessage">{this.state.errorMessage}</p>}
								<PrimaryButton
									className="verifyCardButton"
									text="Verify Card"
									onClick={this.handleVerifyCardButtonClick}
									disabled={this.state.loading}
								/>
							</>
						)}
					</>
				)}
				{this.state.loading && <Loading loadingPosition="fixed" />}
			</>
		);
	}
}

// Must use the stripe prop of injectStripe HOC: https://github.com/stripe/react-stripe-elements#advanced-integrations
interface Props extends ReactStripeElements.InjectedStripeProps {
	stripeCardDetails?: StripeCardDetails;
	cardType: string;
	onVerifyCard: (document: Document) => Promise<boolean>;
	onDeleteButtonClick: (documentType: string) => void;
}

interface State extends AsyncFormState {
	cardName: string;
	isCardVerified: boolean;
}

export default injectStripe(StripeCreditCardForm);
